import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Clock, AlertTriangle } from "lucide-react";

/**
 * PaywallBanner — Phase 3D Monetization
 * Soft-mode (per user spec):
 *   • Trial users see a friendly countdown
 *   • Expired users see a hard upgrade CTA — but routes remain reachable
 *     (read-only mode handled by individual page CTAs).
 * Admins never see it.
 */
export default function PaywallBanner() {
  const { subscription, user } = useAuth();
  if (!subscription || !user) return null;
  if (user.role === "admin") return null;
  const { status, in_trial, days_remaining } = subscription;

  if (status === "active" && !in_trial) return null;

  if (in_trial) {
    // Surface trial only when ≤ 3 days left to avoid noise
    if (days_remaining > 3) return null;
    return (
      <div
        data-testid="paywall-banner-trial"
        className="flex items-center justify-between gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-amber-100"
      >
        <div className="flex items-center gap-2 text-sm">
          <Clock className="size-4" />
          <span>
            <strong className="font-semibold">{days_remaining} day{days_remaining === 1 ? "" : "s"}</strong> left on your free trial.
          </span>
        </div>
        <Link
          to="/billing"
          data-testid="paywall-banner-trial-cta"
          className="rounded-full bg-amber-400/90 px-3 py-1 text-xs font-semibold text-ink-900 hover:bg-amber-300"
        >
          Upgrade plan
        </Link>
      </div>
    );
  }

  // Expired
  return (
    <div
      data-testid="paywall-banner-expired"
      className="flex items-center justify-between gap-3 border-b border-red-500/30 bg-red-500/10 px-4 py-2 text-red-100"
    >
      <div className="flex items-center gap-2 text-sm">
        <AlertTriangle className="size-4" />
        <span>
          Your access has expired. <span className="opacity-80">Upgrade your plan to keep receiving live AI signals.</span>
        </span>
      </div>
      <Link
        to="/billing"
        data-testid="paywall-banner-expired-cta"
        className="inline-flex items-center gap-1 rounded-full bg-gold-grad px-3 py-1 text-xs font-semibold text-ink-900 hover:opacity-90"
      >
        <Sparkles className="size-3.5" />
        Upgrade & Use
      </Link>
    </div>
  );
}
